import React from "react";

/** Reusable admin form fields. */

export const Input = ({ label, value, onChange, type = "text", placeholder, required, testId, className = "" }) => (
  <label className={`block ${className}`}>
    {label ? <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">{label}</div> : null}
    <input
      type={type}
      required={required}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white border border-black/15 focus:border-brand-navy focus:outline-none px-4 py-3 text-brand-dark"
      data-testid={testId}
    />
  </label>
);

export const Textarea = ({ label, value, onChange, rows = 4, placeholder, testId, className = "" }) => (
  <label className={`block ${className}`}>
    {label ? <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">{label}</div> : null}
    <textarea
      rows={rows}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white border border-black/15 focus:border-brand-navy focus:outline-none px-4 py-3 text-brand-dark resize-y"
      data-testid={testId}
    />
  </label>
);

export const Select = ({ label, value, onChange, options, testId, className = "" }) => (
  <label className={`block ${className}`}>
    {label ? <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">{label}</div> : null}
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-black/15 focus:border-brand-navy focus:outline-none px-4 py-3 text-brand-dark"
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

/** Localized text field: renders TR + EN + AR inputs storing into a {tr,en,ar} dict. */
export const Localized = ({ label, value, onChange, textarea = false, testId, langs = ["tr", "en", "ar"] }) => {
  const v = value || {};
  const setLang = (l) => (val) => onChange({ ...v, [l]: val });
  const Field = textarea ? Textarea : Input;
  return (
    <div>
      {label ? <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">{label}</div> : null}
      <div className={`grid grid-cols-1 md:grid-cols-${langs.length} gap-3`}>
        {langs.map((l) => (
          <Field
            key={l}
            label={l.toUpperCase()}
            value={v[l]}
            onChange={setLang(l)}
            testId={testId ? `${testId}-${l}` : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export const Card = ({ title, action, children }) => (
  <div className="bg-white border border-black/5 p-6 md:p-8">
    {title ? (
      <div className="flex items-center justify-between mb-6">
        <div className="font-serif text-xl text-brand-navy">{title}</div>
        {action}
      </div>
    ) : null}
    {children}
  </div>
);

export const AdminHeader = ({ eyebrow, title, action }) => (
  <div className="sticky top-14 lg:top-0 z-20 -mx-6 md:-mx-10 px-6 md:px-10 py-4 mb-8 bg-[var(--brand-light-gray)] border-b border-black/5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
    <div>
      {eyebrow ? <div className="eyebrow eyebrow-gold">{eyebrow}</div> : null}
      <h1 className="display display-md mt-3">{title}</h1>
    </div>
    {action}
  </div>
);

export const Btn = ({ children, variant = "primary", type = "button", onClick, testId, disabled }) => {
  const cls =
    variant === "primary"
      ? "bg-brand-navy text-white border-brand-navy hover:bg-brand-primary hover:border-brand-primary"
      : variant === "danger"
        ? "bg-white text-red-600 border-red-300 hover:bg-red-600 hover:text-white hover:border-red-600"
        : "bg-white text-brand-navy border-brand-navy hover:bg-brand-navy hover:text-white";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-5 py-3 border text-[0.7rem] tracking-[0.22em] uppercase font-medium transition-colors disabled:opacity-60 ${cls}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
};

export const DataTable = ({ columns, rows, onRowClick, empty = "Kayıt bulunamadı." }) => (
  <div className="bg-white border border-black/5 overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="border-b border-black/10">
        <tr>
          {columns.map((c) => (
            <th key={c.key} className="text-left px-6 py-4 text-[0.66rem] tracking-[0.24em] uppercase text-brand-medium font-medium">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="px-6 py-16 text-center text-brand-medium">
              {empty}
            </td>
          </tr>
        ) : (
          rows.map((r, ri) => (
            <tr
              key={r.id || ri}
              className={`border-b border-black/5 ${onRowClick ? "cursor-pointer hover:bg-brand-light/60" : ""}`}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              data-testid={`row-${r.id || ri}`}
            >
              {columns.map((c) => (
                <td key={c.key} className="px-6 py-4 align-middle">
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);
